import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  CubeIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CpuChipIcon,
  CircleStackIcon,
  CodeBracketIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { Environment, Deployment, CodeApplicationCreate, ERPApplicationCreate } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';
import { Button, Badge } from '../components/ui';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

type ApplicationType = 'CODE' | 'ERP';

interface DeploymentForm {
  nom: string;
  cpu: number;
  ram: number;
  replicas: number;
  certificat_tls_id: string | null;
  nom_domaine: string;
  type_application: ApplicationType;
  // CODE fields
  framework?: string;  // Framework ID for CODE apps
  url_repository?: string;
  git_branch?: string;
  build_args?: Record<string, any>;
  // ERP fields
  url_image?: string;
  image_tag?: string;
  helm_values?: Record<string, any>;
  database_config?: Record<string, any>;
  // Champs spécifiques à Odoo
  odoo_email?: string;
  odoo_password?: string;
  postgres_password?: string;
}

// Framework configuration with language, colors, and icons
const FRAMEWORKS = [
  { id: 'python-django', name: 'Django', language: 'Python', color: 'emerald', icon: '🐍' },
  { id: 'python-fastapi', name: 'FastAPI', language: 'Python', color: 'emerald', icon: '🐍' },
  { id: 'python-flask', name: 'Flask', language: 'Python', color: 'emerald', icon: '🐍' },
  { id: 'nodejs-express', name: 'Express', language: 'Node.js', color: 'green', icon: CubeIcon },
  { id: 'nodejs-nestjs', name: 'NestJS', language: 'Node.js', color: 'green', icon: CubeIcon },
  { id: 'react', name: 'React', language: 'Frontend', color: 'blue', icon: CodeBracketIcon },
  { id: 'nextjs', name: 'Next.js', language: 'Frontend', color: 'blue', icon: CodeBracketIcon },
  { id: 'vue', name: 'Vue.js', language: 'Frontend', color: 'blue', icon: '🖖' },
  { id: 'java-springboot', name: 'Spring Boot', language: 'Java', color: 'orange', icon: '☕' },
  { id: 'go-gin', name: 'Gin', language: 'Go', color: 'cyan', icon: '🔷' },
  { id: 'php-laravel', name: 'Laravel', language: 'PHP', color: 'red', icon: '🐘' },
];

const DeploymentsPage: React.FC = () => {
  const { t } = (useTranslation as any)('deployments');
  const { environmentId } = useParams<{ environmentId: string }>();
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<DeploymentForm>();
  const applicationType = watch('type_application', 'CODE');
  const selectedFramework = watch('framework');

  useEffect(() => {
    if (environmentId) {
      loadEnvironmentAndDeployments();
    }
  }, [environmentId]);

  const loadEnvironmentAndDeployments = async () => {
    if (!environmentId) return;

    try {
      setLoading(true);

      const [environmentData, deploymentsData] = await Promise.all([
        apiService.getEnvironment(environmentId),
        apiService.getEnvironmentDeployments(environmentId)
      ]);

      setEnvironment(environmentData);
      setDeployments(deploymentsData);
    } catch (error: unknown) {
      Logger.error('Erreur chargement:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };


  const onSubmit = async (data: DeploymentForm) => {
    if (!environmentId) return;

    setCreating(true);
    try {
      if (data.type_application === 'CODE') {
        const codeData: CodeApplicationCreate = {
          nom: data.nom,
          cpu: data.cpu,
          ram: data.ram,
          replicas: data.replicas,
          certificat_tls_id: data.certificat_tls_id || null,
          nom_domaine: data.nom_domaine || null,
          type_application: 'CODE',
          framework: data.framework!,
          repo_url: data.url_repository!,
          git_branch: data.git_branch!,
          build_args: data.build_args || null,
          image_registry_url: null,
          image_tag: null,
          repository_access: null
        };

        await apiService.deployCodeApplication(environmentId, codeData);
      } else {
        const erpData: ERPApplicationCreate = {
          nom: data.nom,
          cpu: data.cpu,
          ram: data.ram,
          replicas: data.replicas,
          certificat_tls_id: data.certificat_tls_id || null,
          nom_domaine: data.nom_domaine || null,
          type_application: 'ERP',
          image_registry_url: data.url_image!,
          image_tag: data.image_tag!,
          helm_values: data.helm_values || null,
          database_config: data.database_config || null,
          // Configuration spécifique pour Odoo
          ...(data.url_image?.includes('odoo') && {
            odoo_email: data.odoo_email || 'admin@example.com',
            odoo_password: data.odoo_password,
            postgres_password: data.postgres_password
          })
        };

        await apiService.deployERPApplication(environmentId, erpData);
      }

      toast.success(t('deploySuccess'));

      await loadEnvironmentAndDeployments();

      reset();
      setShowCreateForm(false);
    } catch (error: unknown) {
      Logger.error('Erreur déploiement:', error);
      const errorMessage = getApiErrorMessage(error) || t('deployError');
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
      case 'deployed':
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
      case 'building':
      case 'deploying':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-text-quaternary" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto" aria-hidden="true"></div>
        <p className="mt-4 text-text-secondary">{t('loading', { ns: 'common' })}</p>
      </div>
    );
  }

  if (!environment) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{t('envNotFound')}</p>
        <Link to="/projects" className="text-accent-400 hover:text-accent-300">
          {`← ${t('backToProjects')}`}
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-4">
          <li>
            <Link to="/projects" className="text-text-quaternary hover:text-text-tertiary">
              <ArrowLeftIcon className="flex-shrink-0 h-5 w-5" />
              <span className="ml-2">{t('backToProjects')}</span>
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="text-text-quaternary">/</span>
              <Link
                to={`/environments/${environment.projet_id}`}
                className="ml-4 text-text-tertiary hover:text-text-primary"
              >
                {t('nav.environments', { ns: 'common' })}
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <span className="text-text-quaternary">/</span>
              <span className="ml-4 text-text-tertiary">{environment.nom}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="lg:flex lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
            {t('title')}
          </h2>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              <CubeIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
              {t('appCount', { count: deployments.length })}
            </div>
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              {t('environment')} <strong className="ml-1">{environment.nom}</strong>
            </div>
          </div>
        </div>
        <div className="mt-5 flex lg:mt-0 lg:ml-4">
          <Link to={`/deploy?environmentId=${environmentId}`}>
            <Button variant="primary" size="md">
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              {t('newApp')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mt-8 bg-surface-2 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary">
              {t('createForm.title')}
            </h3>
            <div className="mt-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Application Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-3">
                    {t('createForm.appType')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`relative rounded-lg border p-4 cursor-pointer ${
                        applicationType === 'CODE'
                          ? 'border-accent-500 bg-accent-500/12'
                          : 'border-border-1 bg-surface-2 hover:bg-surface-3'
                      }`}
                      onClick={() => setValue('type_application', 'CODE')}
                    >
                      <input
                        {...register('type_application')}
                        type="radio"
                        value="CODE"
                        className="sr-only"
                      />
                      <div className="flex items-center">
                        <CodeBracketIcon className="h-6 w-6 text-accent-500" />
                        <div className="ml-3">
                          <div className="font-medium text-text-primary">CODE</div>
                          <div className="text-sm text-text-tertiary">{t('createForm.codeDescription')}</div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`relative rounded-lg border p-4 cursor-pointer ${
                        applicationType === 'ERP'
                          ? 'border-accent-500 bg-accent-500/12'
                          : 'border-border-1 bg-surface-2 hover:bg-surface-3'
                      }`}
                      onClick={() => setValue('type_application', 'ERP')}
                    >
                      <input
                        {...register('type_application')}
                        type="radio"
                        value="ERP"
                        className="sr-only"
                      />
                      <div className="flex items-center">
                        <RocketLaunchIcon className="h-6 w-6 text-purple-500" />
                        <div className="ml-3">
                          <div className="font-medium text-text-primary">ERP/SAAS</div>
                          <div className="text-sm text-text-tertiary">{t('createForm.erpDescription')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Common Fields */}
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="nom" className="block text-sm font-medium text-text-secondary">
                      {t('createForm.appName')}
                    </label>
                    <input
                      {...register('nom', { required: t('validation.nameRequired') })}
                      type="text"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="mon-application"
                    />
                    {errors.nom && (
                      <p className="mt-1 text-sm text-red-600">{errors.nom.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="cpu" className="block text-sm font-medium text-text-secondary">
                      {t('createForm.cpuCores')}
                    </label>
                    <input
                      {...register('cpu', {
                        required: t('validation.cpuRequired'),
                        min: { value: 0.1, message: t('validation.cpuMin') }
                      })}
                      type="number"
                      step="0.1"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="0.5"
                    />
                    {errors.cpu && (
                      <p className="mt-1 text-sm text-red-600">{errors.cpu.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ram" className="block text-sm font-medium text-text-secondary">
                      {t('createForm.ramGb')}
                    </label>
                    <input
                      {...register('ram', {
                        required: t('validation.ramRequired'),
                        min: { value: 0.5, message: t('validation.ramMin') }
                      })}
                      type="number"
                      step="0.1"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="1.0"
                    />
                    {errors.ram && (
                      <p className="mt-1 text-sm text-red-600">{errors.ram.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="replicas" className="block text-sm font-medium text-text-secondary">
                      {t('createForm.replicas')}
                    </label>
                    <input
                      {...register('replicas', {
                        required: t('validation.replicasRequired'),
                        min: { value: 1, message: t('validation.replicasMin') }
                      })}
                      type="number"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="1"
                      defaultValue={1}
                    />
                    {errors.replicas && (
                      <p className="mt-1 text-sm text-red-600">{errors.replicas.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="nom_domaine" className="block text-sm font-medium text-text-secondary">
                      {t('createForm.domainOptional')}
                    </label>
                    <input
                      {...register('nom_domaine')}
                      type="text"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="mon-app.example.com"
                    />
                    <div className="mt-2 p-3 bg-blue-500/8 border border-blue-500/20 rounded-md">
                      <p className="text-xs text-blue-400 font-medium mb-1">
                        {t('createForm.dnsRequired')}
                      </p>
                      <p className="text-xs text-blue-400/80">
                        Ajoutez une entr&#233;e <span className="font-mono font-semibold">CNAME</span> dans le DNS de votre domaine :
                      </p>
                      <code className="block mt-1 text-xs bg-blue-500/12 text-blue-300 px-2 py-1 rounded font-mono">
                        votre-domaine.com → zone-nubi-1.nubiecloud.io
                      </code>
                    </div>
                  </div>

                </div>

                {/* Type-specific fields */}
                {applicationType === 'CODE' && (
                  <div className="space-y-6">
                    <h4 className="text-md font-medium text-text-primary border-t pt-6">{t('createForm.codeConfig')}</h4>
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-3">
                          {t('createForm.selectFramework')}
                        </label>
                        <input type="hidden" {...register('framework', { required: t('validation.frameworkRequired') })} />
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {FRAMEWORKS.map((framework) => {
                            const isSelected = selectedFramework === framework.id;
                            const colorClasses = {
                              emerald: isSelected
                                ? 'border-emerald-500 bg-emerald-500/12'
                                : 'border-border-1 hover:border-emerald-500/40',
                              green: isSelected
                                ? 'border-green-500 bg-green-500/12'
                                : 'border-border-1 hover:border-green-500/40',
                              blue: isSelected
                                ? 'border-accent-500 bg-accent-500/12'
                                : 'border-border-1 hover:border-accent-500/40',
                              orange: isSelected
                                ? 'border-orange-500 bg-orange-500/12'
                                : 'border-border-1 hover:border-orange-500/40',
                              cyan: isSelected
                                ? 'border-cyan-500 bg-cyan-500/12'
                                : 'border-border-1 hover:border-cyan-500/40',
                              red: isSelected
                                ? 'border-red-500 bg-red-500/12'
                                : 'border-border-1 hover:border-red-500/40',
                            };

                            return (
                              <div
                                key={framework.id}
                                onClick={() => setValue('framework', framework.id)}
                                className={`
                                  relative rounded-lg border-2 p-4 cursor-pointer transition-all
                                  ${colorClasses[framework.color as keyof typeof colorClasses]}
                                  ${isSelected ? 'ring-2 ring-offset-2 ring-accent-500' : ''}
                                `}
                              >
                                <div className="flex flex-col items-center text-center space-y-2">
                                  {typeof framework.icon === 'string' ? (
                                    <span className="text-3xl">{framework.icon}</span>
                                  ) : (
                                    <framework.icon className="h-8 w-8 text-current" />
                                  )}
                                  <div>
                                    <div className="font-semibold text-sm text-text-primary">
                                      {framework.name}
                                    </div>
                                    <div className="text-xs text-text-tertiary mt-0.5">
                                      {framework.language}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-accent-500" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {errors.framework && (
                          <p className="mt-2 text-sm text-red-600">{errors.framework.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="url_repository" className="block text-sm font-medium text-text-secondary">
                          {t('createForm.gitRepoUrl')}
                        </label>
                        <input
                          {...register('url_repository', {
                            required: applicationType === 'CODE' ? t('validation.repoUrlRequired') : false
                          })}
                          type="url"
                          className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="https://github.com/user/repo.git"
                        />
                        {errors.url_repository && (
                          <p className="mt-1 text-sm text-red-600">{errors.url_repository.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="git_branch" className="block text-sm font-medium text-text-secondary">
                          {t('createForm.gitBranch')}
                        </label>
                        <input
                          {...register('git_branch', {
                            required: applicationType === 'CODE' ? t('validation.branchRequired') : false
                          })}
                          type="text"
                          className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="main"
                          defaultValue="main"
                        />
                        {errors.git_branch && (
                          <p className="mt-1 text-sm text-red-600">{errors.git_branch.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {applicationType === 'ERP' && (
                  <div className="space-y-6">
                    <h4 className="text-md font-medium text-text-primary border-t pt-6">{t('createForm.erpConfig')}</h4>
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="url_image" className="block text-sm font-medium text-text-secondary">
                          {t('createForm.dockerImage')}
                        </label>
                        <input
                          {...register('url_image', {
                            required: applicationType === 'ERP' ? t('validation.dockerImageRequired') : false
                          })}
                          type="text"
                          className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="odoo:17.0"
                        />
                        {errors.url_image && (
                          <p className="mt-1 text-sm text-red-600">{errors.url_image.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="image_tag" className="block text-sm font-medium text-text-secondary">
                          {t('createForm.imageTag')}
                        </label>
                        <input
                          {...register('image_tag', {
                            required: applicationType === 'ERP' ? t('validation.imageTagRequired') : false
                          })}
                          type="text"
                          className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="latest"
                          defaultValue="latest"
                        />
                        {errors.image_tag && (
                          <p className="mt-1 text-sm text-red-600">{errors.image_tag.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Champs spécifiques à Odoo si l'image contient 'odoo' */}
                    {watch('url_image')?.includes('odoo') && (
                      <div className="space-y-4 border-t pt-4">
                        <h5 className="text-sm font-medium text-text-primary">{t('createForm.odooConfig')}</h5>
                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="odoo_email" className="block text-sm font-medium text-text-secondary">
                              {t('createForm.odooAdminEmail')}
                            </label>
                            <input
                              {...register('odoo_email')}
                              type="email"
                              className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                              placeholder="admin@example.com"
                            />
                          </div>

                          <div>
                            <label htmlFor="odoo_password" className="block text-sm font-medium text-text-secondary">
                              {t('createForm.odooAdminPassword')}
                            </label>
                            <input
                              {...register('odoo_password')}
                              type="password"
                              className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                              placeholder="Mot de passe Odoo"
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="postgres_password" className="block text-sm font-medium text-text-secondary">
                              {t('createForm.postgresPassword')}
                            </label>
                            <input
                              {...register('postgres_password')}
                              type="password"
                              className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                              placeholder="Mot de passe PostgreSQL"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={creating}
                  >
                    {creating ? t('createForm.deploying') : t('createForm.deployApp')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Deployments List */}
      <div className="mt-8">
        {deployments.length === 0 ? (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
            <h3 className="mt-2 text-sm font-medium text-text-primary">{t('noApps')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('noAppsDescription')}
            </p>
            <div className="mt-6">
              <Link to={`/deploy?environmentId=${environmentId}`}>
                <Button variant="primary" size="md">
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  {t('newApp')}
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {deployments.map((deployment) => (
              <div
                key={deployment.id}
                className="bg-surface-2 overflow-hidden shadow-card rounded-card hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {deployment.type_application === 'CODE' ? (
                        <CodeBracketIcon className="h-6 w-6 text-accent-500" />
                      ) : (
                        <RocketLaunchIcon className="h-6 w-6 text-purple-500" />
                      )}
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-text-tertiary truncate">
                          {deployment.type_application}
                        </dt>
                        <dd className="text-lg font-medium text-text-primary">
                          {deployment.nom}
                        </dd>
                      </dl>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mt-4 flex items-center">
                    {getStatusIcon(deployment.statut_deploiement)}
                    <Badge
                      variant={
                        ['running', 'deployed', 'active'].includes(deployment.statut_deploiement.toLowerCase())
                          ? 'success'
                          : ['failed', 'error'].includes(deployment.statut_deploiement.toLowerCase())
                          ? 'error'
                          : ['pending', 'building', 'deploying'].includes(deployment.statut_deploiement.toLowerCase())
                          ? 'info'
                          : 'neutral'
                      }
                      size="sm"
                      className="ml-2"
                    >
                      {deployment.statut_deploiement}
                    </Badge>
                  </div>

                  {/* Resources */}
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <CpuChipIcon className="h-4 w-4 text-accent-500 mx-auto" />
                      <dt className="text-xs font-medium text-text-tertiary">CPU</dt>
                      <dd className="text-sm font-medium text-text-primary">
                        {deployment.cpu_requis}
                      </dd>
                    </div>
                    <div className="text-center">
                      <CircleStackIcon className="h-4 w-4 text-green-500 mx-auto" />
                      <dt className="text-xs font-medium text-text-tertiary">RAM</dt>
                      <dd className="text-sm font-medium text-text-primary">
                        {deployment.ram_requise}GB
                      </dd>
                    </div>
                    <div className="text-center">
                      <CubeIcon className="h-4 w-4 text-purple-500 mx-auto" />
                      <dt className="text-xs font-medium text-text-tertiary">{t('replicas')}</dt>
                      <dd className="text-sm font-medium text-text-primary">
                        {deployment.replicas}
                      </dd>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="mt-4 space-y-2">
                    {deployment.application_url && (
                      <div className="text-xs">
                        <span className="font-medium text-text-tertiary">URL:</span>{' '}
                        <a
                          href={deployment.application_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-400 hover:text-accent-300 underline inline-flex items-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {deployment.application_url.replace(/^https?:\/\//, '')}
                          <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                          </svg>
                        </a>
                      </div>
                    )}
                    {deployment.nom_de_domaine && (
                      <div className="text-xs text-text-tertiary">
                        <span className="font-medium">{t('domain')}</span> {deployment.nom_de_domaine}
                      </div>
                    )}
                    <div className="text-xs text-text-tertiary">
                      <span className="font-medium">{t('deployedAt')}</span> {formatDate(deployment.date_creation)}
                    </div>
                  </div>
                </div>

                <div className="bg-surface-3 px-5 py-3">
                  <Link
                    to={`/deployment/${deployment.id}`}
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
                    </svg>
                    {t('manageDeployment')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DeploymentsPage;
